/* База упражнений (EX-1): только список — клик по строке открывает страницу элемента
   (TPL-2), там его смотрят и правят. «Добавить упражнение» — пустая страница своего. */
import { mediaUrl, useCatalog } from '@tg/app-core';
import { norm, type Exercise } from '@tg/domain';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GROUP_ORDER, PMNAMES } from '../lib/dict';
import { exIni } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { byNum, byStr, DataTable, type Col } from '../ui/DataTable';
import { ICON } from '../ui/icons';

const V = { q: '', eq: '', own: false, g: 'Все' };

const Thumb = ({ e, size = 44, font = 13 }: { e: Exercise; size?: number; font?: number }) => (
  <span className="thumb" style={{ width: size, height: size, fontSize: font, borderRadius: 'var(--rad-m)' }}>
    {e.img ? <img src={mediaUrl(e.img)} alt="" loading="lazy" /> : exIni(e)}
  </span>
);

export function Exercises() {
  const cat = useCatalog();
  const nav = useNavigate();
  usePageTitle('Упражнения', cat.ready ? cat.list.length : null);
  const [, redraw] = useState(0);
  const set = (f: () => void) => {
    f();
    redraw((x) => x + 1);
  };
  const GROUPS = useMemo(
    () => ['Все', ...GROUP_ORDER.filter((g) => cat.list.some((e) => e.groups.includes(g))), ...[...new Set(cat.list.flatMap((e) => e.groups))].filter((g) => !GROUP_ORDER.includes(g))],
    [cat.list],
  );
  const EQUIP = useMemo(() => {
    const n: Record<string, number> = {};
    cat.list.forEach((e) => (n[e.equipment] = (n[e.equipment] || 0) + 1));
    return Object.keys(n)
      .filter((x) => x !== '—' && x)
      .sort((a, b) => n[b] - n[a])
      .concat(n['—'] ? ['—'] : []);
  }, [cat.list]);
  const match = (e: Exercise) => {
    if (V.g !== 'Все' && !e.groups.includes(V.g)) return false;
    if (V.eq && e.equipment !== V.eq) return false;
    if (V.own && !e.own) return false;
    if (V.q) {
      const n = norm(V.q);
      if (norm(e.ru).indexOf(n) < 0 && norm(e.en).indexOf(n) < 0) return false;
    }
    return true;
  };
  const list = cat.list.filter(match).sort((a, b) => +!!b.own - +!!a.own);
  const cols: Col<Exercise>[] = [
    {
      k: 'ru',
      n: 'Упражнение',
      sort: byStr((e) => e.ru),
      cell: (e) => (
        <div className="cellname">
          <Thumb e={e} />
          <div style={{ minWidth: 0 }}>
            <div className="n">{e.ru}</div>
            <div className="s">{e.en}</div>
          </div>
        </div>
      ),
    },
    { k: 'g', n: 'Группы', w: '140px', sort: byStr((e) => e.groups.join(', ')), cell: (e) => e.groups.join(', ') || '—' },
    { k: 'eq', n: 'Оснащение', w: '130px', sort: byStr((e) => e.equipment), cell: (e) => e.equipment },
    {
      k: 'u',
      n: 'Показатели',
      w: '190px',
      sort: byNum((e) => e.units.length),
      cell: (e) =>
        e.units.map((u, i) => (
          <span key={u}>
            {i > 0 && ' '}
            <span className="chip">{u}</span>
          </span>
        )),
    },
    { k: 'pm', n: 'ПМ', w: '90px', sort: byNum((e) => (e.pm ? 0 : 1)), cell: (e) => (e.pm ? <span className="chip acc">{PMNAMES[e.pm] || e.pm}</span> : '—') },
    { k: 'own', n: 'Источник', w: '110px', sort: byNum((e) => (e.own ? 0 : 1)), cell: (e) => (e.own ? <span className="chip ok">своё</span> : <span className="chip ghost">общая</span>) },
  ];
  return (
    <div className="cons one">
      <main>
        <section className="pane">
          <div className="pane-h">
            <label className="search" style={{ width: 260 }}>
              <ICON.search />
              <input id="q" placeholder="Поиск упражнения…" autoFocus value={V.q} onChange={(x) => set(() => (V.q = x.target.value))} />
            </label>
            <select className="inp" id="g" style={{ width: 190 }} value={V.g} onChange={(x) => set(() => (V.g = x.target.value))}>
              {GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g === 'Все' ? 'Все группы' : g}
                </option>
              ))}
            </select>
            <select className="inp" id="eq" style={{ width: 205 }} value={V.eq} onChange={(x) => set(() => (V.eq = x.target.value))}>
              <option value="">Любое оснащение</option>
              {EQUIP.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
            <label className="chk">
              <input type="checkbox" id="own" checked={V.own} onChange={(x) => set(() => (V.own = x.target.checked))} /> Только свои
            </label>
            <span className="sp"></span>
            <Link className="btn" id="btnAdd" to="/exercises/new">
              <ICON.plus /> Добавить упражнение
            </Link>
          </div>
          {!cat.ready ? null : list.length ? (
            <DataTable id="ex" cols={cols} rows={list} onRow={(x) => nav('/exercises/' + x.id)} fixed />
          ) : (
            <div className="empty">
              <div className="t">Ничего не нашли</div>
              <div className="d">Измените поиск или фильтр</div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
